import $ from 'jquery';
import $script from 'scriptjs';
import debounce from 'just-debounce';
import CurrencyConverter from 'shopify-currency-converter';

import Forms from '../Forms';
import MessageBanner from '../components/MessageBanner';

export default class StaticCart {
  constructor(section) {
    this.settings = section.data.settings;
    this.shipping = section.data.shipping;
    this.messageBanner = new MessageBanner();

    this.$window = $(window);
    this.$el = $(section.el);
    this.$header = $('.site-header-wrapper');
    this.$total = this.$el.find('[data-cart-total]');
    this.$shipping = this.$el.find('[data-cartshipping]');

    // Product form containers
    this.$titleTotalSmall = this.$el.find('.cart-title-total--small');
    this.$titleTotalLarge = this.$el.find('.cart-title-total--large');
    this.$titleTotalContents = this.$el.find('[data-cart-title-total]');

    this._moveTitleTotal();

    const $scripts = $('[data-scripts]');

    $script($scripts.data('shopify-api-url'), () => {
      this._bindEvents();

      Shopify.onError = this._handleErrors.bind(this);
    });

    this.forms = new Forms(this.$el);

    // Update the section when onSectionLoad fires to restore prices to local currency
    this.$el.find('.money').each((index, el) => CurrencyConverter.update(el));

    if (this.settings.shipping && this.$shipping.length) {
      $script($scripts.data('shopify-countries'), () => {
        $script($scripts.data('shopify-common'), () => {
          $script($scripts.data('shopify-cart'), () => {
            this._initShippingCalc();
          });
        });
      });
    }
  }

  onSectionUnload() {
    this.$el.off('.cart-page');
    this.$window.off('.cart-page');
    this.forms.unload();
    this.messageBanner.unload();
    this.messageBanner.dismissBanners();
  }

  _bindEvents() {
    this.$el.on('keydown.cart-page', '[data-cartitem-quantity]', (event) => {
      this._quantityKeyDown(event);
    });

    this.$el.on('change.cart-page', '[data-cartitem-quantity]', (event) => {
      this._editItemQuantity(event);
    });

    this.$el.on('click.cart-page', '[data-cartitem-remove]', (event) => {
      event.preventDefault();
      this._editItemQuantity(event, true);
    });

    this.$window.on('resize.cart-page', debounce(() => this._moveTitleTotal(), 20));
  }

  _moveTitleTotal() {
    if (!this.$titleTotalContents.length) {
      return;
    }

    if (this.$window.outerWidth() >= 480) {
      if (!$.contains(this.$titleTotalLarge[0], this.$titleTotalContents[0])) {
        const $form = this.$titleTotalContents.detach();
        this.$titleTotalLarge.append($form);
      }
    } else {
      if (!$.contains(this.$titleTotalSmall[0], this.$titleTotalContents[0])) {
        const $form = this.$titleTotalContents.detach();
        this.$titleTotalSmall.append($form);
      }
    }
  }

  /**
   * Prevent form submission when pressing `enter` within a quantity selector
   *
   * @param event
   * @private
   */
  _quantityKeyDown(event) {
    // Only block enter keys
    if (event.keyCode !== 13) {
      return;
    }

    // Block form submission, but update field
    $(event.currentTarget).trigger('change');

    event.preventDefault();
  }

  /**
   * Handle an item quantity change
   *
   * @param event
   * @param {Boolean} remove - Set as true to remove cart item
   * @private
   */
  _editItemQuantity(event, remove = false) {
    this.messageBanner.dismissBanners();

    const $target = $(event.currentTarget);
    const $cartItem = $target.closest('[data-cartitem-id]');

    let quantity = remove ? 0 : parseInt($target.val(), 10);
    quantity = Math.max(quantity, 0);

    this._updateCart($cartItem, quantity);

    // Remove item from cart visually if it is being removed
    if (quantity === 0 || remove) {
      $cartItem.height($cartItem.height());
    }
  }

  /**
   * Update cart with a valid quantity
   *
   * @param $cartItem
   * @param quantity
   * @private
   */
  _updateCart($cartItem, quantity) {
    const id = $cartItem.data('cartitem-id');

    // Notify Shopify updated item
    Shopify.changeItem(id, quantity, (response) => {
      this._didUpdate(id, quantity,response, $cartItem);
    });
  }

  _quantityError(limit, title) {
    const errorMessage = this.settings.stock_limit
      .replace('** quantity **', limit)
      .replace('** title **', title);

    this.messageBanner.message(errorMessage, 'error');
  }

  /**
   * Update cart content based on information from Shopify
   *
   * @param id
   * @param quantity
   * @param response
   * @param $cartItem
   * @returns {*}
   * @private
   */
  _didUpdate(id, quantity,response, $cartItem) {
    // Reload page if all items are removed from cart
    if (!response.items.length) {
      return window.location = '/cart';
    }

    this.$header.trigger('cartcount:update', { response });

    this.$total.each((i, el) => {
      el.innerHTML = Shopify.formatMoney(response.total_price, this.settings.money_format);
      CurrencyConverter.update(el);
    });

    // Select item from response
    const filteredItems = response.items.filter(item => item.id === id);

    if(!filteredItems.length) {
      return $cartItem.addClass('removing')
        .one('trend', () => {
          $cartItem
            .off('trend')
            .remove();
        });
    }

    const cartItem = filteredItems[0];
    const itemTotal = $cartItem[0].querySelector('[data-cartitem-total]');

    if (cartItem.quantity !== quantity) {
      this._quantityError(cartItem.quantity, cartItem.title);
      $cartItem.find('[data-quantity-input]').val(cartItem.quantity);
      $cartItem.find('[data-quantity-select]').val(cartItem.quantity);
    }

    itemTotal.innerHTML = Shopify.formatMoney(cartItem.price * cartItem.quantity, this.settings.money_format);
    CurrencyConverter.update(itemTotal);
  }

  /**
   * Handle Errors returned from Shopify
   *
   * @param xhr
   * @private
   */
  _handleErrors(xhr = null) {
    if(!xhr) {
      return;
    }

    const errors = $.parseJSON(xhr.responseText);

    if (errors.zip.length > 0) {
      // Hide the response so that it can be populated smoothly
      this._hideShippingResponse();

      // Empty out contents
      this.$shippingResponseMessage.empty();
      this.$shippingResponseRates.empty();

      if (errors.zip[0].indexOf('is not valid') !== -1 || errors.zip[0].indexOf('can\'t be blank') !== -1) {
        this.$shippingResponseMessage.html(`${this.shipping.zip} ${errors.zip}`);
      }

      this._showShippingResponse();
      this._enableShippingButton();
    }
  }

  _initShippingCalc() {
    this.$shippingToggle = this.$el.find('[data-cartshipping-toggle]');
    this.$shippingResponse = this.$shipping.find('[data-cartshipping-response]');
    this.$shippingResponseMessage = this.$shippingResponse.find('[data-cartshipping-message]');
    this.$shippingResponseRates = this.$shippingResponse.find('[data-cartshipping-rates]');
    this.$shippingSubmit = this.$shipping.find('[data-cartshipping-submit]');

    this._bindShippingCalcEvents();

    Shopify.Cart.ShippingCalculator.show({
      submitButton: this.shipping.calculate_shipping,
      submitButtonDisabled: this.shipping.calculating,
      customerIsLoggedIn: this.shipping.customer,
      moneyFormat: this.settings.money_format,
    });
  }

  _bindShippingCalcEvents() {
    this.$el.on('click.cart-page', '[data-cartshipping-toggle]', () => {
      this._toggleShippingCalc();
    });

    this.$el.on('click.cart-page', '[data-cartshipping-submit]', () => {
      this._getShippingRates();
    });

    this.$el.on('keypress.cart-page', '#address_zip', (event) => {
      if (event.keyCode === 10 || event.keyCode === 13) {
        event.preventDefault();
        this.$shippingSubmit.trigger('click');
      }
    });
  }

  _toggleShippingCalc() {
    const oldText = this.$shippingToggle.text();
    const newText = this.$shippingToggle.data('cartshipping-toggle');

    this.$shippingToggle
      .text(newText)
      .data('cartshipping-toggle', oldText);

    this.$shipping.toggleClass('open');
  }

  _getShippingRates() {
    this._disableShippingButton();

    const shippingAddress = {};
    shippingAddress.country = $('#address_country').val() || '';
    shippingAddress.province = $('#address_province').val() || '';
    shippingAddress.zip = $('#address_zip').val() || '';

    //Creates an ajax request which returns shipping information
    Shopify.getCartShippingRatesForDestination(shippingAddress, (response, shippingAddress) => {
      const addressBase = [];

      if (shippingAddress.zip.length) {
        addressBase.push(shippingAddress.zip.trim());
      }

      if (shippingAddress.province.length) {
        addressBase.push(shippingAddress.province);
      }

      if (shippingAddress.country.length) {
        addressBase.push(shippingAddress.country);
      }

      const address = addressBase.join(', ');

      // Hide the response so that it can be populated smoothly
      this._hideShippingResponse();

      // Empty out contents
      this.$shippingResponseMessage.empty();
      this.$shippingResponseRates.empty();

      let responseText = '';

      if (response.length > 1) {
        const firstRate = Shopify.Cart.ShippingCalculator.formatRate(response[0].price);
        responseText = this.shipping.multiple_rates
          .replace('*address*', address)
          .replace('*number_of_rates*', response.length)
          .replace('*rate*', `<span class="money">${firstRate}</span>`);
      } else if (response.length === 1) {
        responseText = this.shipping.one_rate.replace('*address*', address);
      } else {
        responseText = this.shipping.no_rates;
      }

      this.$shippingResponseMessage.html(responseText);

      for (let i = 0; i < response.length; i++) {
        const rate = response[i];
        const price = Shopify.Cart.ShippingCalculator.formatRate(rate.price);
        const rateValue = this.shipping.rate_value
          .replace('*rate_title*', rate.name)
          .replace('*rate*', `<span class="money">${price}</span>`);

        this.$shippingResponseRates.append(`<li>${rateValue}</li>`);
      }

      // Reset the calculating button so it can be used again
      this._enableShippingButton();

      // Show the response
      this._showShippingResponse();
    });
  }

  _enableShippingButton() {
    this.$shippingSubmit
      .text(this.shipping.calculate_shipping)
      .attr('disabled', false);
  }

  _disableShippingButton() {
    this.$shippingSubmit
      .text(this.shipping.calculating)
      .attr('disabled', true);
  }

  _showShippingResponse() {
    this.$shippingResponse.addClass('visible');
  }

  _hideShippingResponse() {
    this.$shippingResponse.removeClass('visible');
  }
}
